import type { ServerEvent } from "./events";
import { parseEvent } from "./events";

export type ConnectionStatus =
  "connecting" | "open" | "reconnecting" | "offline" | "closed";

/** Application-level "you are not signed in", sent by `BaseAuthedConsumer`. */
export const UNAUTHENTICATED = 4401;

/** Ours, for a socket we decided is dead because nothing came back. */
const STALE = 4000;

export type SocketOptions = {
  url: string;
  onEvent: (event: ServerEvent) => void;
  onStatusChange: (status: ConnectionStatus) => void;
  /** The session is gone. Reconnecting would loop forever against a 4401. */
  onUnauthenticated: () => void;
  /** Called on every open *after* the first — see `RealtimeProvider`. */
  onReconnect: () => void;
  /** Injectable for tests. */
  createWebSocket?: (url: string) => WebSocket;
  baseDelayMs?: number;
  maxDelayMs?: number;
  heartbeatMs?: number;
};

/**
 * One socket per tab, reconnecting on its own.
 *
 * Server pushes only: nothing the seller *does* goes over this. Writes are REST
 * calls, where DRF's permissions and serializer validation already live
 * (.docs/04 §5).
 */
export class RealtimeSocket {
  private socket: WebSocket | null = null;
  private attempt = 0;
  private hasConnected = false;
  private stopped = false;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastMessageAt = 0;

  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly heartbeatMs: number;
  private readonly createWebSocket: (url: string) => WebSocket;

  constructor(private readonly options: SocketOptions) {
    this.baseDelayMs = options.baseDelayMs ?? 1_000;
    this.maxDelayMs = options.maxDelayMs ?? 30_000;
    this.heartbeatMs = options.heartbeatMs ?? 25_000;
    this.createWebSocket = options.createWebSocket ?? ((url) => new WebSocket(url));
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    // 1000 = normal closure. `handleClose` reads this to know the disconnect
    // was ours and must not be retried.
    this.socket?.close(1000, "client stop");
    this.socket = null;
    this.options.onStatusChange("closed");
  }

  /**
   * Try again right now, discarding any pending backoff.
   *
   * Called when the browser reports it is back online or the tab becomes
   * visible. Waiting out a 30-second backoff after the user has physically
   * come back to the screen is the difference between "instant" and "broken".
   */
  reconnectNow(): void {
    if (this.stopped || this.socket?.readyState === WebSocket.OPEN) return;
    this.attempt = 0;
    this.clearReconnectTimer();
    this.connect();
  }

  private connect(): void {
    if (this.stopped) return;

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      // No point burning backoff attempts against a disconnected machine; the
      // `online` listener in `RealtimeProvider` calls `reconnectNow`.
      this.options.onStatusChange("offline");
      return;
    }

    this.options.onStatusChange(this.hasConnected ? "reconnecting" : "connecting");

    let socket: WebSocket;
    try {
      socket = this.createWebSocket(this.options.url);
    } catch {
      this.scheduleReconnect();
      return;
    }
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.lastMessageAt = Date.now();
      this.options.onStatusChange("open");
      this.startHeartbeat();
      if (this.hasConnected) this.options.onReconnect();
      this.hasConnected = true;
    };

    socket.onmessage = (message: MessageEvent<string>) => {
      this.lastMessageAt = Date.now();
      const event = parseEvent(message.data);
      // `pong` is liveness only; it has already done its job by arriving.
      if (event && event.type !== "pong") this.options.onEvent(event);
    };

    socket.onerror = () => {
      // Browsers give no detail here by design. `onclose` always follows, and
      // that is where the reconnect belongs — doing it in both would double up.
    };

    socket.onclose = (event: CloseEvent) => this.handleClose(event);
  }

  private handleClose(event: CloseEvent): void {
    this.stopHeartbeat();
    this.socket = null;
    if (this.stopped) return;

    if (event.code === UNAUTHENTICATED) {
      // The consumer refused us. Retrying cannot help and would hammer the
      // server for as long as the tab stays open.
      this.options.onStatusChange("closed");
      this.options.onUnauthenticated();
      return;
    }

    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.stopped || this.reconnectTimer !== null) return;
    this.options.onStatusChange(this.hasConnected ? "reconnecting" : "connecting");
    const delay = this.delayFor(this.attempt);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /**
   * Exponential backoff with full jitter.
   *
   * The jitter is not decoration. Every seller's tab loses the socket at the
   * same instant when the server restarts, and without it they all come back
   * in the same millisecond and knock it over again.
   */
  private delayFor(attempt: number): number {
    const ceiling = Math.min(this.maxDelayMs, this.baseDelayMs * 2 ** attempt);
    return Math.random() * ceiling;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      const socket = this.socket;
      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      // A socket dropped by a proxy or a sleeping laptop stays `OPEN` on this
      // side and never fires `onclose`. Silence past two beats is the only
      // evidence available, so treat it as dead and force the reconnect.
      if (Date.now() - this.lastMessageAt > this.heartbeatMs * 2) {
        socket.close(STALE, "no response");
        return;
      }
      socket.send(JSON.stringify({ type: "ping" }));
    }, this.heartbeatMs);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer !== null) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = null;
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer);
    this.reconnectTimer = null;
  }

  private clearTimers(): void {
    this.clearReconnectTimer();
    this.stopHeartbeat();
  }
}

/**
 * Same origin as the page, scheme matched to it.
 *
 * `AllowedHostsOriginValidator` on the server checks the Origin header, so a
 * hard-coded host would be refused in every environment but the one it names.
 */
export function presenceUrl(location: Location = window.location): string {
  const scheme = location.protocol === "https:" ? "wss:" : "ws:";
  return `${scheme}//${location.host}/ws/presence/`;
}
